import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  deleteTournament,
  publishTournament,
  unpublishTournament,
  openRegistration,
  closeRegistration,
  reopenRegistration,
} from '@/lib/api';
import { ApiError } from '@/lib/api/core';
import { showErrorToast } from '@/lib/toast';
import type { Tournament } from '@swiss-magic/shared-types';

// Story 083 — single-tournament lifecycle controller for the hub.
// Mirrors the dashboard's per-list controller but acts on the one
// tournament the hub is showing. Owns the confirm-dialog state; the
// dialogs themselves render once in the layout route, and the panels
// / sub-pages trigger actions through the outlet context. Accepts a
// nullable tournament because the layout mounts before the fetch
// resolves — every action no-ops until it's loaded.

export function useTournamentLifecycle(
  tournament: Tournament | null,
  reload: () => Promise<void>,
  godMode = false,
) {
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishWithRegistration, setPublishWithRegistration] = useState(false);
  const [closeRegOpen, setCloseRegOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  async function run(fn: () => Promise<unknown>, fallback: string) {
    if (!tournament) return;
    try {
      await fn();
      await reload();
    } catch (err) {
      showErrorToast(err instanceof ApiError ? err.userMessage : fallback);
    }
  }

  // Publish + open registration in one step for a draft; otherwise
  // just reopen the registration window.
  async function handleOpenRegistration() {
    if (!tournament) return;
    await run(
      () =>
        tournament.status === 'draft'
          ? publishTournament(tournament.id, true)
          : openRegistration(tournament.id),
      'Failed to open registration',
    );
  }

  async function handleReopenRegistration() {
    await run(
      () => reopenRegistration(tournament!.id),
      'Failed to reopen registration',
    );
  }

  async function handleUnpublish() {
    await run(() => unpublishTournament(tournament!.id), 'Failed to unpublish');
  }

  // Publish-only (director chooses whether to open registration via
  // the dialog checkbox).
  async function confirmPublish() {
    if (!tournament) return;
    try {
      await publishTournament(tournament.id, publishWithRegistration);
      await reload();
    } catch (err) {
      showErrorToast(
        err instanceof ApiError ? err.userMessage : 'Failed to publish',
      );
    } finally {
      setPublishOpen(false);
      setPublishWithRegistration(false);
    }
  }

  // Try to close; if pending registrations remain the backend reports
  // a count and we open the approve/reject dialog instead of advancing.
  async function handleCloseRegistration() {
    if (!tournament) return;
    try {
      const result = await closeRegistration(tournament.id);
      if (result.pendingCount) {
        setPendingCount(result.pendingCount);
        setCloseRegOpen(true);
        return;
      }
      await reload();
    } catch (err) {
      showErrorToast(
        err instanceof ApiError ? err.userMessage : 'Failed to close registration',
      );
    }
  }

  async function closeWithPendingAction(action: 'approve' | 'reject') {
    if (!tournament) return;
    try {
      await closeRegistration(tournament.id, { pendingAction: action });
      await reload();
    } catch (err) {
      showErrorToast(
        err instanceof ApiError ? err.userMessage : 'Failed to close registration',
      );
    } finally {
      setCloseRegOpen(false);
      setPendingCount(0);
    }
  }

  // Deletion leaves no tournament to return to — bounce to dashboard.
  async function confirmDelete() {
    if (!tournament) return;
    try {
      // Story 107 — god mode may delete at any stage; drafts use the
      // normal path so the backend gate stays exercised.
      await deleteTournament(
        tournament.id,
        godMode && tournament.status !== 'draft',
      );
      navigate('/dashboard');
    } catch (err) {
      showErrorToast(
        err instanceof ApiError ? err.userMessage : 'Failed to delete',
      );
      setDeleteOpen(false);
    }
  }

  return {
    deleteOpen,
    setDeleteOpen,
    publishOpen,
    setPublishOpen,
    publishWithRegistration,
    setPublishWithRegistration,
    closeRegOpen,
    setCloseRegOpen,
    pendingCount,
    handleOpenRegistration,
    handleReopenRegistration,
    handleUnpublish,
    handleCloseRegistration,
    closeWithPendingAction,
    confirmPublish,
    confirmDelete,
  };
}

export type TournamentLifecycle = ReturnType<typeof useTournamentLifecycle>;
